import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import apiClient from 'src/Common/apiClient'
import '../../login/login.scss'
import logo from 'src/assets/images/1journey.png'
import art from 'src/assets/images/readingbook.png'

/* --- inline icons (no icon-font dependency) --- */
const LockIcon = () => (
  <svg
    className="jl__lead"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="4" y="10.5" width="16" height="10" rx="2.5" />
    <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
  </svg>
)
const EyeIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
)
const EyeOffIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9.9 4.2A9.6 9.6 0 0 1 12 4c6.5 0 10 7 10 7a15.8 15.8 0 0 1-3 3.7M6.1 6.1A15.8 15.8 0 0 0 2 11s3.5 7 10 7a9.6 9.6 0 0 0 3.5-.6M3 3l18 18M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  </svg>
)
const ArrowIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
)

function ChangePassword() {
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [oldPasswordVisible, setOldPasswordVisible] = useState(false)
  const [newPasswordVisible, setNewPasswordVisible] = useState(false)
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  const handleCancelClick = () => {
    navigate('/studentDashboard')
  }

  const handleChangePassword = async () => {
    setError('')
    setMessage('')

    // Validate required fields
    if (!oldPassword) {
      setError('Old password is required.')
      return
    }
    if (!newPassword) {
      setError('New password is required.')
      return
    }
    if (!confirmPassword) {
      setError('Confirm password is required.')
      return
    }

    // Validate new password length
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.')
      return
    }

    // Validate new password and confirm password match
    if (newPassword !== confirmPassword) {
      setError('New password and confirm password do not match.')
      return
    }

    try {
      setLoading(true)
      const response = await apiClient.post('/Account/Change-password', {
        oldPassword: oldPassword,
        newPassword: newPassword,
      })

      if (response.status === 200) {
        navigate('/login', {
          state: { successMessage: 'Password changed successfully' },
        })
        setOldPassword('')
        setNewPassword('')
        setConfirmPassword('')
      } else {
        setError('Failed to change password. Please try again.')
      }
    } catch (error) {
      console.error('Error changing password:', error)
      setError(error.response.data)
    } finally {
      setLoading(false)
    }
  }

  const cancelBtnStyle = {
    height: '46px',
    border: '1px solid #e2e7f1',
    borderRadius: '10px',
    background: '#fff',
    color: '#3a4763',
    fontFamily: 'inherit',
    fontSize: '0.9rem',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'border-color 0.18s, background 0.18s, color 0.18s',
  }

  return (
    <div className="jl">
      <div className="jl__shell">
        {/* ---------- Brand / hero ---------- */}
        <aside className="jl__hero jl__hero--art">
          <div className="jl__aurora" aria-hidden="true">
            <span className="jl__orb jl__orb--a" />
            <span className="jl__orb jl__orb--b" />
            <span className="jl__orb jl__orb--c" />
            <span className="jl__beam" />
            <span className="jl__grid" />
          </div>

          <div className="jl__art">
            <span className="jl__art-glow" aria-hidden="true" />
            <img src={art} alt="A student reading" className="jl__art-img" />
            <div className="jl__art-tag">
              <span>1Journey</span>
              <strong>Stay with us</strong>
            </div>
          </div>
        </aside>

        {/* ---------- Form ---------- */}
        <main className="jl__panel">
          <div className="jl__card">
            <img src={logo} alt="1Journey" className="jl__card-logo" />
            <span className="jl__eyebrow">Change password</span>
            <h2 className="jl__title">Change your password</h2>
            <p className="jl__sub">Enter your current password and choose a new one.</p>

            {message && (
              <p
                style={{
                  color: '#16a34a',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  margin: '0 0 0.9rem',
                }}
              >
                {message}
              </p>
            )}
            {error && (
              <p className="jl__err" style={{ margin: '0 0 0.9rem' }}>
                {error}
              </p>
            )}

            <div className="jl__form">
              {/* Old Password */}
              <div className="jl__field">
                <label htmlFor="cp-old">Old password</label>
                <div className="jl__control">
                  <LockIcon />
                  <input
                    id="cp-old"
                    type={oldPasswordVisible ? 'text' : 'password'}
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    placeholder="Enter your old password"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="jl__eye"
                    onClick={() => setOldPasswordVisible(!oldPasswordVisible)}
                    aria-label={oldPasswordVisible ? 'Hide password' : 'Show password'}
                  >
                    {oldPasswordVisible ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div className="jl__field">
                <label htmlFor="cp-new">New password</label>
                <div className="jl__control">
                  <LockIcon />
                  <input
                    id="cp-new"
                    type={newPasswordVisible ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="jl__eye"
                    onClick={() => setNewPasswordVisible(!newPasswordVisible)}
                    aria-label={newPasswordVisible ? 'Hide password' : 'Show password'}
                  >
                    {newPasswordVisible ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="jl__field">
                <label htmlFor="cp-confirm">Confirm password</label>
                <div className="jl__control">
                  <LockIcon />
                  <input
                    id="cp-confirm"
                    type={confirmPasswordVisible ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your new password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="jl__eye"
                    onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                    aria-label={confirmPasswordVisible ? 'Hide password' : 'Show password'}
                  >
                    {confirmPasswordVisible ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              {/* Buttons */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                <button type="button" onClick={handleCancelClick} style={cancelBtnStyle}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="jl__btn"
                  onClick={handleChangePassword}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="jl__spinner" aria-hidden="true" /> Changing&hellip;
                    </>
                  ) : (
                    <>
                      Change password <ArrowIcon />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <p className="jl__copy">© {new Date().getFullYear()} 1Journey · Learn, Grow, Achieve.</p>
        </main>
      </div>
    </div>
  )
}

export default ChangePassword
